import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  CircularProgress,
  FormControlLabel,
  Switch,
  Box,
} from '@mui/material';
import { useDataProvider, useNotify, useRefresh } from 'react-admin';

interface MaterialEditDialogProps {
  open: boolean;
  onClose: () => void;
  materialId: string;
  onSuccess?: (material: any) => void;
}

const MaterialEditDialog: React.FC<MaterialEditDialogProps> = ({
  open,
  onClose,
  materialId,
  onSuccess,
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<any>({});

  const [form, setForm] = useState({
    name: '',
    unit: '',
    cost: 0,
    description: '',
    isActive: true,
  });

  useEffect(() => {
    if (open && materialId) {
      setLoading(true);
      dataProvider
        .getOne('materials', { id: materialId })
        .then(({ data }) => {
          setForm({
            name: data.name || '',
            unit: data.unit || '',
            cost: data.cost ?? 0,
            description: data.description || '',
            isActive: !!data.isActive,
          });
        })
        .catch(() => {
          notify('Error loading material', { type: 'error' });
          onClose();
        })
        .finally(() => setLoading(false));
    }
  }, [open, materialId, dataProvider, notify, onClose]);

  const handleChange = (field: string, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = () => {
    const newErr: any = {};
    if (!form.name || !form.name.trim()) { newErr.name = 'Name is required'; }
    if (isNaN(Number(form.cost))) { newErr.cost = 'Cost must be a number'; }
    setErrors(newErr);
    return Object.keys(newErr).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) { return; }
    setSaving(true);
    try {
      const payload: any = {
        name: form.name,
        unit: form.unit || undefined,
        cost: Number(form.cost),
        description: form.description || undefined,
        isActive: !!form.isActive,
      };

      const { data } = await dataProvider.update('materials', {
        id: materialId, data: payload,
        previousData: {}
      });

      notify('Material updated', { type: 'success' });
      refresh();
      if (onSuccess) { onSuccess(data); }
      onClose();
    } catch (error: any) {
      notify(error?.message || 'Failed to update material', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Material</DialogTitle>
      <DialogContent>
        <Box sx={{ position: 'relative' }}>
          {(loading || saving) && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'rgba(255,255,255,0.7)',
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 1,
              }}
            >
              <CircularProgress />
            </Box>
          )}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Name"
                value={form.name}
                onChange={(e) => handleChange('name', e.target.value)}
                fullWidth
                required
                error={!!errors.name}
                helperText={errors.name}
                disabled={loading || saving}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Unit"
                value={form.unit}
                onChange={(e) => handleChange('unit', e.target.value)}
                fullWidth
                disabled={loading || saving}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Cost"
                value={form.cost}
                onChange={(e) => handleChange('cost', e.target.value)}
                fullWidth
                error={!!errors.cost}
                helperText={errors.cost}
                disabled={loading || saving}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Description"
                value={form.description}
                onChange={(e) => handleChange('description', e.target.value)}
                fullWidth
                multiline
                rows={3}
                disabled={loading || saving}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!form.isActive}
                    onChange={(e) => handleChange('isActive', e.target.checked)}
                    disabled={loading || saving}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving || loading}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving || loading}>
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MaterialEditDialog;
